/**
 * Admin: Trade Partner access applications queue.
 *
 *  - Lists partner_users with status filter (pending → approved/active → rejected)
 *  - Approve: POST /api/admin/partner-users/{id}/approve
 *    → backend generates a set-password link + emails it; we surface
 *      the raw link in the row so admin can copy/paste if delivery
 *      failed silently (same UX pattern as the admin invite flow).
 *  - Reject with optional reason
 *  - Hard-delete (cleanup of test rows)
 */
import React, { useEffect, useState } from 'react';
import { Check, Copy, History, KeyRound, Link as LinkIcon, Mail, Trash2, UserCheck, UserX } from 'lucide-react';
import api from '../lib/api';
import HelpCallout from './HelpCallout';

function fmtDate(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' }); }
  catch { return iso; }
}

const STATUS_BADGE = {
  pending: 'bg-mustard/20 text-charcoal',
  approved: 'bg-blue-100 text-blue-900',
  active: 'bg-green-100 text-green-900',
  rejected: 'bg-charcoal/15 text-charcoal/70',
};

function StakeholderListPanel() {
  const [audit, setAudit] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [open, setOpen] = useState(false);
  const load = async () => {
    setBusy(true);
    try { setAudit((await api.get('/admin/stakeholders/audit')).data); }
    catch { setMsg({ ok: false, text: 'Could not reach EmailOctopus — try again shortly.' }); }
    setBusy(false);
  };
  const syncAll = async () => {
    if (!window.confirm(`Add ${audit.missing_count} missing business contact${audit.missing_count === 1 ? '' : 's'} to the "${audit.list_name}" list in EmailOctopus?`)) return;
    setBusy(true); setMsg(null);
    try {
      const r = (await api.post('/admin/stakeholders/sync', {})).data;
      setMsg({ ok: true, text: `Added ${r.added_count} contact${r.added_count === 1 ? '' : 's'}${r.failed.length ? ` · ${r.failed.length} failed` : ''}.` });
      await load();
    } catch (e) { setMsg({ ok: false, text: e.response?.data?.detail || 'Sync failed.' }); }
    setBusy(false);
  };
  return (
    <div className="bg-white border border-charcoal/10 rounded-2xl p-5" data-testid="stakeholder-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-xl flex items-center gap-2"><Mail size={16} className="text-heritage"/> EmailOctopus stakeholder list</h2>
          <p className="text-xs text-charcoal/60 mt-1">
            Approved partners are added to the <strong>LPL Partner database</strong> list automatically.
            Use the check below to find businesses on the website that are missing from the list.
          </p>
        </div>
        <button onClick={() => { setOpen(true); load(); }} disabled={busy} data-testid="stakeholder-audit-btn"
          className="bg-forest text-base text-sm font-semibold px-5 py-2.5 rounded-full disabled:opacity-50">
          {busy ? 'Checking…' : 'Check for missing businesses'}
        </button>
      </div>
      {msg && <div className={`text-sm mt-3 font-semibold ${msg.ok ? 'text-forest' : 'text-heritage'}`} data-testid="stakeholder-msg">{msg.text}</div>}
      {open && audit && (
        <div className="mt-4" data-testid="stakeholder-audit-results">
          <div className="text-sm text-charcoal/75 mb-3">
            {audit.on_list_count.toLocaleString()} subscribed on the list · {audit.website_contacts} business contacts on the website ·{' '}
            <strong className={audit.missing_count ? 'text-heritage' : 'text-forest'}>{audit.missing_count} missing</strong>
          </div>
          {audit.missing_count > 0 && (
            <>
              <button onClick={syncAll} disabled={busy} data-testid="stakeholder-sync-btn"
                className="bg-heritage text-base text-sm font-semibold px-5 py-2.5 rounded-full mb-3 disabled:opacity-50">
                Add all {audit.missing_count} to EmailOctopus
              </button>
              <div className="max-h-64 overflow-y-auto border border-charcoal/10 rounded-xl">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-stone">
                    <tr className="text-left text-xs uppercase tracking-wide text-charcoal/50">
                      <th className="px-3 py-2">Business</th><th className="px-3 py-2">Email</th><th className="px-3 py-2">Source</th>
                    </tr>
                  </thead>
                  <tbody>
                    {audit.missing.map((m) => (
                      <tr key={m.email} className="border-t border-charcoal/5">
                        <td className="px-3 py-1.5">{m.business}</td>
                        <td className="px-3 py-1.5 text-charcoal/70">{m.email}</td>
                        <td className="px-3 py-1.5 text-xs text-charcoal/50">{m.source}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
          {audit.missing_count === 0 && <div className="text-sm text-forest font-semibold">Everyone on the website is already on the list. ✓</div>}
        </div>
      )}
    </div>
  );
}

function VenueSearch({ selected, onPick }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  useEffect(() => {
    if (q.trim().length < 2) { setResults([]); return undefined; }
    const t = setTimeout(() => {
      api.get(`/admin/attractions?search=${encodeURIComponent(q.trim())}&limit=8`)
        .then((r) => setResults(r.data.items || [])).catch(() => {});
    }, 300);
    return () => clearTimeout(t);
  }, [q]);
  return (
    <div>
      <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search venues by name or town…"
        data-testid="venue-search-input"
        className="w-full border border-charcoal/20 rounded-xl px-3 py-2 text-sm mb-2" />
      {selected && (
        <div className="text-xs bg-green-50 border border-green-200 rounded-lg px-3 py-2 mb-2" data-testid="venue-search-selected">
          Linking to: <strong>{selected.name}</strong> ({selected.town || 'no town'}){!selected.published && ' · draft'}
        </div>
      )}
      <ul className="space-y-1 max-h-40 overflow-y-auto">
        {results.map((a) => (
          <li key={a.slug}>
            <button type="button" onClick={() => onPick(a)} data-testid={`venue-pick-${a.slug}`}
              className="w-full text-left text-sm px-3 py-1.5 rounded-lg hover:bg-stone flex justify-between gap-2">
              <span className="truncate">{a.name}</span>
              <span className="text-xs text-charcoal/50 whitespace-nowrap">{a.town}{a.member_tier ? ` · ${a.member_tier}` : ''}</span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function PartnerVenueModal({ partner, mode, onClose, onDone }) {
  const [tier, setTier] = useState(partner.membership_tier || '');
  const [venueMode, setVenueMode] = useState('new');
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const submit = async () => {
    setErr('');
    if ((mode === 'assign' || venueMode === 'existing') && !selected) { setErr('Pick a venue from the search first.'); return; }
    setBusy(true);
    try {
      if (mode === 'assign') {
        const r = await api.post(`/admin/partner-users/${partner.id}/assign-venue`, { attraction_slug: selected.slug });
        onDone({ assigned: r.data.attraction });
      } else {
        const payload = { membership_tier: tier || null };
        if (venueMode === 'existing') payload.attraction_slug = selected.slug;
        if (venueMode === 'skip') payload.skip_venue = true;
        const r = await api.post(`/admin/partner-users/${partner.id}/approve`, payload);
        onDone({ id: partner.id, link: r.data.set_password_link, email_status: r.data.email_status, membership_tier: r.data.membership_tier, attraction: r.data.attraction });
      }
    } catch (e) {
      setErr(e.response?.data?.detail || 'Something went wrong');
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-charcoal/50 z-50 flex items-center justify-center p-4" data-testid="partner-venue-modal">
      <div className="bg-white rounded-3xl p-7 max-w-lg w-full shadow-xl">
        <h3 className="font-display text-2xl mb-1">{mode === 'assign' ? 'Assign a venue' : `Approve ${partner.business_name}`}</h3>
        <p className="text-xs text-charcoal/60 mb-5">
          {mode === 'assign'
            ? `Link an existing venue listing to ${partner.email} so they can manage it from their portal.`
            : 'Approving emails them a one-time set-password link. Choose their membership level and how to handle their venue listing.'}
        </p>
        {mode === 'approve' && (
          <>
            <div className="mb-4">
              <div className="text-xs font-semibold uppercase tracking-wider text-charcoal/50 mb-1.5">Membership level</div>
              <select value={tier} onChange={(e) => setTier(e.target.value)} data-testid="approve-tier-select"
                className="w-full border border-charcoal/20 rounded-xl px-3 py-2 text-sm">
                <option value="">None (basic listing rules apply)</option>
                <option value="bronze">Bronze</option>
                <option value="silver">Silver</option>
                <option value="gold">Gold</option>
              </select>
            </div>
            <div className="mb-4">
              <div className="text-xs font-semibold uppercase tracking-wider text-charcoal/50 mb-1.5">Venue listing</div>
              <div className="space-y-1.5 text-sm">
                {[['existing', 'Link to an existing venue on the site'],
                  ['new', `Create a new draft venue ("${partner.business_name}")`],
                  ['skip', 'Skip for now — assign a venue later']].map(([v, label]) => (
                  <label key={v} className="flex items-center gap-2 cursor-pointer">
                    <input type="radio" name="venue-mode" checked={venueMode === v} onChange={() => setVenueMode(v)} data-testid={`venue-mode-${v}`} />
                    {label}
                  </label>
                ))}
              </div>
            </div>
          </>
        )}
        {(mode === 'assign' || venueMode === 'existing') && <VenueSearch selected={selected} onPick={setSelected} />}
        {venueMode === 'new' && mode === 'approve' && !tier && (
          <p className="text-xs text-mustard-700 text-charcoal/60 mb-2">A new draft venue is only auto-created when a membership level is chosen.</p>
        )}
        {err && <p className="text-sm text-red-700 mt-2" data-testid="partner-venue-modal-error">{err}</p>}
        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} className="text-sm px-4 py-2 rounded-full border border-charcoal/20" data-testid="partner-venue-modal-cancel">Cancel</button>
          <button onClick={submit} disabled={busy} data-testid="partner-venue-modal-confirm"
            className="bg-heritage text-base text-sm font-semibold px-5 py-2 rounded-full disabled:opacity-50">
            {busy ? 'Working…' : mode === 'assign' ? 'Link venue' : 'Approve partner'}
          </button>
        </div>
      </div>
    </div>
  );
}

const ACTIVITY_STATUS_CLS = {
  approved: 'bg-green-100 text-green-800',
  pending: 'bg-mustard/25 text-charcoal',
  rejected: 'bg-red-100 text-red-700',
  archived: 'bg-charcoal/10 text-charcoal/60',
};

function ActivityBadge({ status }) {
  return <span className={`text-[10px] font-bold uppercase rounded-full px-2 py-0.5 ${ACTIVITY_STATUS_CLS[status] || 'bg-charcoal/10'}`}>{status}</span>;
}

function PartnerActivity({ partnerId }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    api.get(`/admin/partner-users/${partnerId}/activity`)
      .then((r) => setData(r.data))
      .catch(() => setErr('Could not load activity'));
  }, [partnerId]);
  if (err) return <p className="text-xs text-red-700 mt-3">{err}</p>;
  if (!data) return <p className="text-xs text-charcoal/50 mt-3">Loading activity…</p>;
  const { edits, events, listing_requests: reqs } = data;
  const empty = !edits.length && !events.length && !reqs.length;
  return (
    <div className="mt-3 border-t border-charcoal/10 pt-3 grid md:grid-cols-3 gap-4 text-sm" data-testid={`partner-activity-${partnerId}`}>
      {empty && <p className="text-xs text-charcoal/50 md:col-span-3">No activity yet — no edits, events or listing requests from this account.</p>}
      {edits.length > 0 && (
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-charcoal/50 mb-1.5">Listing edits ({edits.length})</div>
          <ul className="space-y-1.5">
            {edits.slice(0, 8).map((e) => (
              <li key={e.id} className="flex items-start gap-2">
                <ActivityBadge status={e.status} />
                <span className="min-w-0">
                  <span className="block truncate">{e.attraction_slug} — {e.fields_changed.join(', ') || 'no fields'}</span>
                  <span className="text-[11px] text-charcoal/50">{fmtDate(e.submitted_at)}{e.reviewed_by ? ` · reviewed by ${e.reviewed_by}` : ''}</span>
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {events.length > 0 && (
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-charcoal/50 mb-1.5">Event submissions ({events.length})</div>
          <ul className="space-y-1.5">
            {events.slice(0, 8).map((e) => (
              <li key={e.id} className="flex items-start gap-2">
                <ActivityBadge status={e.status} />
                <span className="min-w-0">
                  <span className="block truncate">{e.title}</span>
                  <span className="text-[11px] text-charcoal/50">event date {(e.start_date || '').slice(0, 10)} · submitted {fmtDate(e.created_at)}</span>
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {reqs.length > 0 && (
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-charcoal/50 mb-1.5">Listing requests ({reqs.length})</div>
          <ul className="space-y-1.5">
            {reqs.slice(0, 8).map((r) => (
              <li key={r.slug} className="flex items-start gap-2">
                <ActivityBadge status={r.published ? 'approved' : 'pending'} />
                <span className="min-w-0">
                  <span className="block truncate">{r.name}</span>
                  <span className="text-[11px] text-charcoal/50">requested {fmtDate(r.created_at)}</span>
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export default function AdminPartnerUsers() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState('pending');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState('');
  const [lastApproval, setLastApproval] = useState(null); // {id, link}
  const [copiedFor, setCopiedFor] = useState(null);
  const [modal, setModal] = useState(null); // {partner, mode: 'approve'|'assign'}
  const [activityFor, setActivityFor] = useState(null);

  async function load() {
    setErr('');
    try {
      const q = filter ? `?status=${filter}` : '';
      const r = await api.get(`/admin/partner-users${q}`);
      setItems(r.data.items || []);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Failed to load partner applications');
    }
  }

  useEffect(() => { load(); }, [filter]);

  async function resendLink(id, tier) {
    setBusy(`approve-${id}`); setErr('');
    try {
      const r = await api.post(`/admin/partner-users/${id}/approve`, { membership_tier: tier || null, skip_venue: true });
      setLastApproval({ id, link: r.data.set_password_link, email_status: r.data.email_status });
      await load();
    } catch (e) {
      setErr(e.response?.data?.detail || 'Could not re-send link');
    } finally { setBusy(''); }
  }

  async function resetPassword(id, email) {
    if (!window.confirm(`Issue a password reset link for ${email}? They'll get an email with a one-time link (also shown here to copy).`)) return;
    setBusy(`reset-${id}`); setErr('');
    try {
      const r = await api.post(`/admin/partner-users/${id}/reset-password`);
      setLastApproval({ id, link: r.data.set_password_link, email_status: r.data.email_status, is_reset: true });
      await load();
    } catch (e) {
      setErr(e.response?.data?.detail || 'Could not issue reset link');
    } finally { setBusy(''); }
  }

  async function reject(id) {
    const reason = window.prompt('Optional reason (will be stored on the record, not emailed):', '') ?? '';
    if (reason === null) return;
    setBusy(`reject-${id}`); setErr('');
    try {
      await api.post(`/admin/partner-users/${id}/reject`, { reason });
      await load();
    } catch (e) {
      setErr(e.response?.data?.detail || 'Could not reject partner');
    } finally { setBusy(''); }
  }

  async function remove(id, email) {
    if (!window.confirm(`Delete partner application for ${email}? This cannot be undone.`)) return;
    setBusy(`delete-${id}`); setErr('');
    try {
      await api.delete(`/admin/partner-users/${id}`);
      await load();
    } catch (e) {
      setErr(e.response?.data?.detail || 'Could not delete');
    } finally { setBusy(''); }
  }

  async function copyLink(link, id) {
    try {
      await navigator.clipboard.writeText(link);
      setCopiedFor(id);
      setTimeout(() => setCopiedFor(null), 2000);
    } catch (e) {
      // Clipboard rejection (insecure context etc.) — surface a manual prompt
      window.prompt('Copy the link below:', link);
    }
  }

  return (
    <div className="space-y-6" data-testid="admin-partner-users-page">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl mb-1">Trade Partner Applications</h1>
          <p className="text-sm text-charcoal/65">Approve businesses to send them a set-password link, or reject if they&apos;re not a fit.</p>
        </div>
        <div className="flex gap-2">
          {['pending', 'approved', 'active', 'rejected', ''].map((s) => (
            <button
              key={s || 'all'}
              type="button"
              onClick={() => setFilter(s)}
              data-testid={`filter-${s || 'all'}`}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border ${
                filter === s
                  ? 'bg-heritage text-base border-heritage'
                  : 'border-charcoal/15 text-charcoal/70 hover:border-charcoal/30'
              }`}
            >
              {s ? s[0].toUpperCase() + s.slice(1) : 'All'}
            </button>
          ))}
        </div>
      </header>

      <HelpCallout>
        Approving a partner generates a one-time, 7-day password-set link and emails it to them.
        The link is also shown below so you can copy and forward it manually if Resend silently
        fails to deliver.
      </HelpCallout>

      <StakeholderListPanel />

      {err && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-2">{err}</div>}

      {/* Sticky banner: most recently issued set-password link.
          Rendered ABOVE the list (not inside a row) so it stays
          visible even when the approved row falls out of the
          current status filter. */}
      {lastApproval && lastApproval.link && (
        <div className="bg-mustard/15 border border-mustard rounded-2xl p-5" data-testid="last-approval-banner">
          <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
            <div>
              <div className="text-xs uppercase tracking-wider text-charcoal/65 mb-1">{lastApproval.is_reset ? 'Password reset link issued' : 'Set-password link issued'}</div>
              <div className="text-xs text-charcoal/70">
                Email status: <strong>{lastApproval.email_status?.ok ? 'sent \u2713' : (lastApproval.email_status?.skipped ? 'email provider not configured — copy the link below' : 'not sent — copy the link below')}</strong>
                {' '}— this link is single-use and expires in 7 days.
                {lastApproval.membership_tier && lastApproval.attraction && (
                  <div className="mt-1">
                    Added to Members as <strong className="uppercase">{lastApproval.membership_tier}</strong> ({lastApproval.attraction.action} attraction <code className="bg-white px-1 rounded">{lastApproval.attraction.slug}</code>).
                  </div>
                )}
              </div>
            </div>
            <button
              type="button"
              onClick={() => setLastApproval(null)}
              data-testid="dismiss-approval-banner"
              className="text-charcoal/55 hover:text-charcoal text-xs underline"
            >
              Dismiss
            </button>
          </div>
          <div className="flex items-center gap-2">
            <code className="flex-1 text-xs bg-white border border-charcoal/15 rounded-lg px-3 py-2 truncate" data-testid={`set-password-link-${lastApproval.id}`}>{lastApproval.link}</code>
            <button
              type="button"
              onClick={() => copyLink(lastApproval.link, lastApproval.id)}
              data-testid={`copy-link-${lastApproval.id}`}
              className="inline-flex items-center gap-1.5 bg-charcoal text-base text-xs font-semibold rounded-full px-3 py-2 hover:bg-heritage"
            >
              {copiedFor === lastApproval.id ? <><Check size={12} /> Copied</> : <><Copy size={12} /> Copy</>}
            </button>
          </div>
        </div>
      )}

      {modal && (
        <PartnerVenueModal
          partner={modal.partner}
          mode={modal.mode}
          onClose={() => setModal(null)}
          onDone={async (result) => {
            setModal(null);
            if (result.link) setLastApproval(result);
            await load();
          }}
        />
      )}

      {items.length === 0 ? (
        <div className="text-sm text-charcoal/55 text-center py-16 border border-dashed border-charcoal/15 rounded-2xl" data-testid="empty-state">
          No {filter || 'partner'} applications.
        </div>
      ) : (
        <ul className="space-y-3" data-testid="partner-users-list">
          {items.map((p) => {
            return (
              <li key={p.id} className="bg-white border border-charcoal/10 rounded-2xl p-5" data-testid={`partner-row-${p.id}`}>
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-semibold text-charcoal" data-testid={`partner-name-${p.id}`}>{p.business_name}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_BADGE[p.status] || 'bg-charcoal/10'}`} data-testid={`partner-status-${p.id}`}>{p.status}</span>
                    </div>
                    <div className="text-sm text-charcoal/75">
                      {p.contact_name} · <a className="text-heritage hover:underline" href={`mailto:${p.email}`}>{p.email}</a>
                    </div>
                    <div className="text-xs text-charcoal/55 mt-1">
                      Applied {fmtDate(p.created_at)}
                      {p.approved_at && ` \u00b7 Approved ${fmtDate(p.approved_at)}${p.approved_by ? ` by ${p.approved_by}` : ''}`}
                      {p.activated_at && ` \u00b7 Account active since ${fmtDate(p.activated_at)}`}
                      {p.last_login && ` \u00b7 Last login ${fmtDate(p.last_login)}`}
                    </div>
                    <div className="text-xs text-charcoal/55 mt-0.5">
                      {p.membership_tier && <span className="uppercase font-semibold text-heritage mr-2">{p.membership_tier}</span>}
                      {p.linked_attraction_id ? 'Linked to a venue listing' : 'No linked listing yet'}
                      {p.password_reset_at && ` \u00b7 Last reset ${fmtDate(p.password_reset_at)}${p.password_reset_by ? ` by ${p.password_reset_by}` : ''}`}
                    </div>
                    {p.rejection_reason && (
                      <div className="text-xs text-charcoal/65 mt-1 italic">Reason: {p.rejection_reason}</div>
                    )}
                  </div>
                  <div className="flex gap-2">
                    {(p.status === 'pending') && (
                      <button
                        type="button"
                        onClick={() => setModal({ partner: p, mode: 'approve' })}
                        disabled={busy === `approve-${p.id}`}
                        data-testid={`approve-${p.id}`}
                        className="inline-flex items-center gap-1.5 bg-heritage text-base text-sm font-semibold rounded-full px-4 py-2 hover:bg-charcoal disabled:opacity-50"
                      >
                        <UserCheck size={14} /> Approve
                      </button>
                    )}
                    {(p.status === 'pending' || p.status === 'approved') && (
                      <button
                        type="button"
                        onClick={() => reject(p.id)}
                        disabled={busy === `reject-${p.id}`}
                        data-testid={`reject-${p.id}`}
                        className="inline-flex items-center gap-1.5 border border-charcoal/20 text-charcoal text-sm rounded-full px-4 py-2 hover:bg-stone disabled:opacity-50"
                      >
                        <UserX size={14} /> Reject
                      </button>
                    )}
                    {(p.status === 'approved') && (
                      <button
                        type="button"
                        onClick={() => resendLink(p.id, p.membership_tier)}
                        disabled={busy === `approve-${p.id}`}
                        data-testid={`resend-${p.id}`}
                        className="inline-flex items-center gap-1.5 border border-heritage text-heritage text-sm rounded-full px-4 py-2 hover:bg-heritage hover:text-base disabled:opacity-50"
                        title="Re-issue the set-password link and email it again"
                      >
                        <Mail size={14} /> Re-send link
                      </button>
                    )}
                    {(p.status === 'active' || p.status === 'approved') && (
                      <button
                        type="button"
                        onClick={() => setActivityFor(activityFor === p.id ? null : p.id)}
                        data-testid={`activity-${p.id}`}
                        className={`inline-flex items-center gap-1.5 border text-sm rounded-full px-4 py-2 hover:bg-stone ${activityFor === p.id ? 'border-heritage text-heritage' : 'border-charcoal/20 text-charcoal'}`}
                        title="View this partner's edits, event submissions and listing requests"
                      >
                        <History size={14} /> Activity
                      </button>
                    )}
                    {(p.status === 'active' || p.status === 'approved') && (
                      <button
                        type="button"
                        onClick={() => setModal({ partner: p, mode: 'assign' })}
                        data-testid={`assign-venue-${p.id}`}
                        className="inline-flex items-center gap-1.5 border border-charcoal/20 text-charcoal text-sm rounded-full px-4 py-2 hover:bg-stone"
                        title={p.linked_attraction_id ? 'Change the venue linked to this account' : 'Link a venue listing to this account'}
                      >
                        <LinkIcon size={14} /> {p.linked_attraction_id ? 'Change venue' : 'Assign venue'}
                      </button>
                    )}
                    {(p.status === 'active' || p.status === 'approved') && (
                      <button
                        type="button"
                        onClick={() => resetPassword(p.id, p.email)}
                        disabled={busy === `reset-${p.id}`}
                        data-testid={`reset-password-${p.id}`}
                        className="inline-flex items-center gap-1.5 border border-charcoal/20 text-charcoal text-sm rounded-full px-4 py-2 hover:bg-stone disabled:opacity-50"
                        title="Issue a one-time password reset link and email it to the partner"
                      >
                        <KeyRound size={14} /> Reset password
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => remove(p.id, p.email)}
                      disabled={busy === `delete-${p.id}`}
                      data-testid={`delete-${p.id}`}
                      className="inline-flex items-center justify-center w-9 h-9 rounded-full border border-charcoal/15 text-charcoal/55 hover:text-red-700 hover:border-red-300"
                      aria-label="Delete partner record"
                      title="Delete record"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
                {activityFor === p.id && <PartnerActivity partnerId={p.id} />}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
